import type {
  TemplateMappingKindResource,
  TemplateMappingResource,
  TemplateResource,
} from '@/features/legends/api/resources/templates'
import type { LegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import {
  mapUnknownResourceToElement,
  UnknownElementError,
} from '@/features/legends/editor/legend-document'
import {
  mapValueTypeResourceToValueType,
  mapValueTypeToResource,
} from '@/features/legends/editor/value-type-mappers'
import type {
  Template,
  TemplateDataSource,
  TemplateMapping,
  TemplateMappingKind,
} from './templates'

/**
 * Maps a scoping template, fixing references to legacy question IDs.
 *
 * @param resource
 * @param ids
 */
export function mapTemplateResourceToTemplate(
  resource: TemplateResource,
  ids: LegacyIdFixer,
): Template {
  return {
    id: resource.id,
    name: resource.name,
    mappings: resource.mappings?.map((mapping): TemplateMapping => ({
      id: mapping.id,
      questionId: ids.resolve(mapping.questionId),
      kind: mapMappingKindResource(mapping.kind),
    })),
    dataSources: resource.template_data_sources?.map(
      (source): TemplateDataSource => ({
        id: source.id,
        valueType: mapValueTypeResourceToValueType(source.valueType),
      }),
    ),
  }
}

/**
 * Maps a scoping template to the API's.
 *
 * @param template
 */
export function mapTemplateToResource(template: Template): TemplateResource {
  return {
    id: template.id,
    mappings: template.mappings?.map((mapping): TemplateMappingResource => ({
      id: mapping.id,
      kind: mapMappingKindToResource(mapping.kind),
      questionId: mapping.questionId,
    })),
    template_data_sources: template.dataSources?.map((source) => ({
      id: source.id,
      valueType: mapValueTypeToResource(source.valueType),
    })),
    name: template.name,
  }
}

function mapMappingKindResource(
  resource: TemplateMappingKindResource,
): TemplateMappingKind {
  switch (resource.type) {
    case 'Expr':
      return { type: 'Expr', expr: resource.expr }
    case 'Manual':
      return { type: 'Manual' }
  }
  return mapUnknownResourceToElement(resource)
}

function mapMappingKindToResource(
  kind: TemplateMappingKind,
): TemplateMappingKindResource {
  switch (kind.type) {
    case 'Expr':
      return { type: 'Expr', expr: kind.expr }
    case 'Manual':
      return { type: 'Manual' }
    case 'Unknown':
      throw new UnknownElementError('template mapping', kind)
  }
}
